/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useEffect, useState } from "react";
import SwiperCore, { A11y, Keyboard, Mousewheel, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css";
import "swiper/swiper.min.css";

import Product from "web/Layout/ProductCard";
import __ from "web/Layout/Translations";

import useAccessibleSwiperCarousel from "web/hooks/useAccessibleSwiperCarousel";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import type { PropsWithClasses } from "web/types/Common";
import type { ProductInList } from "web/types/Product";

import classify from "web/classify";

import defaultClasses from "./productCarousel.scss";

SwiperCore.use([Mousewheel, Navigation, A11y, Keyboard]);

interface IProductCarouselProps {
  products: ProductInList[];
  listingPosition?: number;
  listingAmount?: number;
  listingCategory?: string;
}

const ProductCarousel: FC<PropsWithClasses<IProductCarouselProps>> = ({
  products = [],
  classes = {},
  listingPosition = null,
  listingAmount = null,
  listingCategory = "",
}) => {
  const statusId = `carousel-status-${listingPosition}-${listingAmount}-${listingCategory}`;
  const [isSwiperLooped, setSwiperLoop] = useState(false);

  const totalSlides = products.length;
  const { swiperRef, swiperA11yProps, containerProps } =
    useAccessibleSwiperCarousel({ deps: [products, isSwiperLooped] });

  useEffect(() => {
    const container = containerProps.ref?.current;
    if (container) {
      const prev = container.querySelector(".swiper-button-prev");
      const next = container.querySelector(".swiper-button-next");

      const swiperContainer = container.querySelector(".swiper-container");

      if (swiperContainer) {
        (swiperContainer as HTMLElement).style.position = "static";
      }

      if (prev && next) {
        prev.setAttribute("tabindex", "0");
        next.setAttribute("tabindex", "0");
        prev.setAttribute("aria-disabled", "false");
        next.setAttribute("aria-disabled", "false");
      }
    }
  }, [containerProps.ref]);

  return isArrayHasItems(products) ? (
    <div className={classes.carouselContainer} {...containerProps}>
      <div id={statusId} aria-live="polite" className="sr-only" />
      <Swiper
        slidesPerView="auto"
        mousewheel={{ forceToAxis: true, sensitivity: 2 }}
        navigation
        watchOverflow
        centerInsufficientSlides
        loop={isSwiperLooped}
        aria-label={__("Karuzela produktów")}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          swiper.keyboard.disable();
        }}
        onInit={(swiper) => {
          // @ts-ignore
          if (swiper?.size < swiper?.virtualSize) {
            setSwiperLoop(true);
          }
        }}
        onSlideChange={(swiper) => {
          const active = swiper.realIndex + 1;
          const live = document.getElementById(statusId);

          if (live) {
            live.textContent = `${__("Slajd")} ${active} ${__(
              "z"
            )} ${totalSlides}`;
          }
        }}
        wrapperTag="ul"
        {...swiperA11yProps}
      >
        {products.map((product, index) => {
          const positionIndex = index + 1;

          return (
            <SwiperSlide
              key={positionIndex}
              role="group"
              aria-roledescription="slide"
              aria-label={`Slajd ${positionIndex} z ${totalSlides}`}
              tag="li"
            >
              <Product
                product={product}
                positionNumber={positionIndex}
                listingPosition={listingPosition as number}
                listingAmount={listingAmount as number}
                listingCategory={listingCategory}
                variant="promo"
              />
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  ) : null;
};

export default classify<PropsWithClasses<IProductCarouselProps>>(
  defaultClasses
)(ProductCarousel);
